interface TreeNode {
  id?: number
  node_uuid: string
  type: string
  ancestor_id?: number | null
  name: string
  description?: string | null
  children?: TreeNode[]
}

interface RoleNode {
  id: number
  node_uuid: string
  type: 'jobcatalog_treerole'
  name: string
}

interface LevelNode {
  id: number
  node_uuid: string
  type: 'jobcatalog_treelevel'
  name: string
}

interface EntitySelectSubItem {
  subId: string
  subName: string
  subSearchKeys: string[]
}

interface EntitySelectEntity {
  id: string
  name: string
  expanded: boolean
  subItems: EntitySelectSubItem[]
}

const findRoles = (node: TreeNode): RoleNode[] => {
  const roles: RoleNode[] = []

  if (node.type === 'jobcatalog_treerole') {
    roles.push({
      id: node.id || 0,
      node_uuid: node.node_uuid,
      type: 'jobcatalog_treerole',
      name: node.name,
    })
  }

  const children = node.children || []
  for (const child of children) {
    roles.push(...findRoles(child))
  }

  return roles
}

const findRoleNode = (node: TreeNode, roleNodeUuid: string): TreeNode | null => {
  if (node.node_uuid === roleNodeUuid && node.type === 'jobcatalog_treerole') {
    return node
  }

  const children = node.children || []
  for (const child of children) {
    const found = findRoleNode(child, roleNodeUuid)
    if (found) return found
  }

  return null
}

const findRoleContext = (
  node: TreeNode,
  roleNodeUuid: string,
  currentFamily?: string,
  currentFunction?: string
): { familyName?: string; functionName?: string } => {
  if (node.node_uuid === roleNodeUuid) {
    return { familyName: currentFamily, functionName: currentFunction }
  }

  const children = node.children || []
  for (const child of children) {
    let family = currentFamily
    let func = currentFunction

    if (node.type === 'jobcatalog_treefamily') family = node.name
    if (node.type === 'jobcatalog_treefunction') func = node.name

    const result = findRoleContext(child, roleNodeUuid, family, func)
    if (result.familyName && result.functionName) return result
  }

  return {}
}

const buildFamilyFunctionMap = (node: TreeNode): Map<string, Set<string>> => {
  const familyFunctionMap = new Map<string, Set<string>>()

  const traverse = (currentNode: TreeNode) => {
    if (currentNode.type === 'jobcatalog_treefamily' && currentNode.children) {
      const functions = new Set<string>()
      currentNode.children.forEach((child) => {
        if (child.type === 'jobcatalog_treefunction') {
          functions.add(child.name)
        }
      })
      if (functions.size > 0) {
        familyFunctionMap.set(currentNode.name, functions)
      }
    }

    const children = currentNode.children || []
    children.forEach((child) => traverse(child))
  }

  traverse(node)
  return familyFunctionMap
}

export const formatJobCatalogRoles = (serializedTree: string): EntitySelectEntity[] => {
  if (!serializedTree || serializedTree.trim() === '') {
    return []
  }

  const tree: TreeNode = JSON.parse(serializedTree)

  if (!tree) {
    return []
  }

  const allRoles = findRoles(tree)
  const familyFunctionMap = buildFamilyFunctionMap(tree)
  const roleGroups = new Map<string, Map<string, RoleNode[]>>()

  allRoles.forEach((role) => {
    const { familyName, functionName } = findRoleContext(tree, role.node_uuid)

    if (familyName && functionName) {
      if (!roleGroups.has(familyName)) {
        roleGroups.set(familyName, new Map())
      }
      const familyGroup = roleGroups.get(familyName)
      if (familyGroup) {
        if (!familyGroup.has(functionName)) {
          familyGroup.set(functionName, [])
        }
        const functionGroup = familyGroup.get(functionName)
        if (functionGroup) {
          functionGroup.push(role)
        }
      }
    }
  })

  const entities: EntitySelectEntity[] = []

  familyFunctionMap.forEach((functions, familyName) => {
    functions.forEach((functionName) => {
      const roles = roleGroups.get(familyName)?.get(functionName) || []
      const rolesWithLevels = roles.filter(
        (role) => getLevelsByRole(tree, role.node_uuid).length > 0
      )

      if (rolesWithLevels.length > 0) {
        entities.push({
          id: `group_${familyName}_${functionName}`,
          name: `${familyName} | ${functionName}`,
          expanded: true,
          subItems: rolesWithLevels.map((role) => ({
            subId: role.node_uuid,
            subName: role.name,
            subSearchKeys: [
              role.name,
              familyName,
              functionName,
              `${familyName} ${functionName}`,
              `${familyName} ${functionName} ${role.name}`,
            ],
          })),
        })
      }
    })
  })

  return entities
}

export const getLevelsByRole = (serializedTree: TreeNode, roleNodeUuid: string): LevelNode[] => {
  const roleNode = findRoleNode(serializedTree, roleNodeUuid)

  if (!roleNode?.children) return []

  return roleNode.children
    .filter((child) => child.type === 'jobcatalog_treelevel')
    .map((child) => ({
      id: child.id || 0,
      node_uuid: child.node_uuid,
      type: 'jobcatalog_treelevel',
      name: child.name,
    }))
}
